import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Check, ShieldCheck, Wallet, Clock, KeyRound, Loader2, CalendarCheck } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { useApp } from '@/context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONSULT_CALENDLY = 'https://calendly.com/medialabs-info/strategic-architecture-call';

const TRUST_POINTS = [
  { icon: ShieldCheck, title: 'A senior team, fully managed', desc: 'We design, build, host and maintain your software so you never manage engineers or chase freelancers.' },
  { icon: Wallet, title: 'Predictable monthly pricing', desc: 'One flat subscription—no surprise invoices. Extra work is always quoted and approved first.' },
  { icon: KeyRound, title: 'You own your business data', desc: 'Custom software IP follows your approved contract. No lock-in games, just clear agreements.' },
  { icon: Clock, title: 'Fast, honest timelines', desc: 'An eligible first version can launch in as little as 15 business days after scope approval.' },
];

function captureAttribution(searchParams) {
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];
  const attribution = {};
  keys.forEach((k) => { const v = searchParams.get(k); if (v) attribution[k] = v; });
  try { if (document.referrer) attribution.referrer = document.referrer; } catch (e) { /* noop */ }
  return attribution;
}

export default function LandingPageRoute() {
  const navigate = useNavigate();
  const { plans } = useApp();
  const [searchParams] = useSearchParams();
  const isPaidTraffic = searchParams.get('paid') === '1';
  const planParam = (searchParams.get('plan') || '').toLowerCase();

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', plan: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (planParam && ['basic', 'premium', 'elite', 'custom'].includes(planParam)) {
      setForm((f) => ({ ...f, plan: planParam }));
    }
  }, [planParam]);

  const planOptions = (plans || []).map((p) => ({ id: p.id, name: p.name }));

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name) { setError('Please enter your name.'); return; }
    if (!email || !email.includes('@')) { setError('Please enter a valid email address.'); return; }
    setSubmitting(true); setError('');

    const planLabel = planOptions.find((p) => p.id === form.plan)?.name || 'Not sure yet';
    const payload = {
      name,
      email,
      phone: form.phone.trim(),
      company: '',
      currency: 'USD',
      timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return ''; } })(),
      application: {
        objective: `Managed development subscription — interested in: ${planLabel}`,
        problem: form.message.trim(),
        goal: form.message.trim(),
        planInterest: form.plan || 'undecided',
        source: 'landing_page',
        attribution: captureAttribution(searchParams),
      },
    };

    try {
      await axios.post(`${API}/webhooks/lead-capture`, payload);
      // Preserve existing tracking: fire the Meta Pixel Lead conversion.
      if (typeof window.fbq === 'function') {
        try { window.fbq('track', 'Lead', { content_name: 'Landing Page Lead' }); } catch (err) { /* noop */ }
      }
      setDone(true);
    } catch (err) {
      setError('Something went wrong. Please try again, or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page-route">
      <SEOHead
        title="Grow Your Business — We'll Handle the Technology | MediaLabs"
        description="You've got a business to grow, not a development team to manage. Tell us what you need and get a fully managed development subscription with predictable pricing."
        path="/landing-page"
      />

      {/* Ambient light background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-32 -left-24 w-[520px] h-[520px] bg-brand-blue/8 rounded-full blur-[130px]" />
        <div className="absolute top-20 -right-24 w-[420px] h-[420px] bg-brand-orange/6 rounded-full blur-[120px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-16">
        {!isPaidTraffic && (
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="landing-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* ── Left: Trust-building ── */}
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">A fully managed development partner</span>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-[2.9rem] font-extrabold tracking-tight text-foreground" style={{ lineHeight: 1.1 }} data-testid="landing-headline">
              You've Got a Business to Grow.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-orange">Not a Development Team to Manage.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Tell us what you want to build or improve. We'll handle the technology end-to-end on a
              simple managed subscription—so you can focus on customers, revenue and growth.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              {TRUST_POINTS.map((t) => {
                const Icon = t.icon;
                return (
                  <div key={t.title} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-brand-blue/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4.5 h-4.5 text-brand-blue" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">{t.title}</p>
                      <p className="text-[13px] text-muted-foreground leading-relaxed mt-0.5">{t.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="w-4 h-4 text-brand-blue" /> No obligation. No technical jargon. Just a clear plan and a predictable price.
            </div>
          </div>

          {/* ── Right: Short lead form / success ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="lg:sticky lg:top-28"
          >
            {done ? (
              <div className="rounded-2xl border border-[color:var(--hairline)] bg-white p-7 sm:p-8 shadow-[var(--shadow-lift)] text-center" data-testid="lead-success">
                <div className="w-14 h-14 rounded-full bg-brand-blue/10 flex items-center justify-center mx-auto mb-4">
                  <CalendarCheck className="w-7 h-7 text-brand-blue" />
                </div>
                <h2 className="text-xl font-bold text-foreground">Thanks{form.name ? `, ${form.name.split(' ')[0]}` : ''} — we've got it.</h2>
                <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                  Our team will review your note and reach out shortly. Prefer to talk now? Book a
                  free consultation at a time that suits you.
                </p>
                <a href={CONSULT_CALENDLY} target="_blank" rel="noopener noreferrer"
                   className="mt-5 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl bg-brand-orange text-white text-sm font-bold hover:bg-brand-orange-dark transition-colors duration-200"
                   data-testid="lead-book-consult">
                  Book a free consultation <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/services" className="mt-3 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl border border-[color:var(--hairline)] text-foreground text-sm font-semibold hover:bg-muted transition-colors duration-200">
                  Explore plans while you wait
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl border border-[color:var(--hairline)] bg-white p-6 sm:p-7 shadow-[var(--shadow-lift)]" data-testid="lead-form">
                <h2 className="text-lg font-bold text-foreground">Tell us what you need</h2>
                <p className="text-sm text-muted-foreground mt-1 mb-5">Two minutes. No tech knowledge required.</p>

                <div className="space-y-4">
                  <div>
                    <label className="text-[13px] font-semibold text-foreground mb-1.5 block">Your name</label>
                    <input value={form.name} onChange={onChange('name')} placeholder="Jane Santos"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[color:var(--hairline)] bg-white text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/15 transition-[border-color,box-shadow] duration-200"
                      data-testid="lead-name" />
                  </div>
                  <div>
                    <label className="text-[13px] font-semibold text-foreground mb-1.5 block">Work email</label>
                    <input type="email" value={form.email} onChange={onChange('email')} placeholder="jane@company.com"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[color:var(--hairline)] bg-white text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/15 transition-[border-color,box-shadow] duration-200"
                      data-testid="lead-email" />
                  </div>
                  <div>
                    <label className="text-[13px] font-semibold text-foreground mb-1.5 block">Phone <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input value={form.phone} onChange={onChange('phone')} placeholder="+63 ..."
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[color:var(--hairline)] bg-white text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/15 transition-[border-color,box-shadow] duration-200"
                      data-testid="lead-phone" />
                  </div>
                  <div>
                    <label className="text-[13px] font-semibold text-foreground mb-1.5 block">What do you want to build or improve?</label>
                    <textarea value={form.message} onChange={onChange('message')} rows={3} placeholder="e.g. A booking app for my clinic, or a dashboard for my team…"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[color:var(--hairline)] bg-white text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/15 transition-[border-color,box-shadow] duration-200 resize-none"
                      data-testid="lead-message" />
                  </div>
                  <div>
                    <label className="text-[13px] font-semibold text-foreground mb-1.5 block">Plan you're interested in <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <select value={form.plan} onChange={onChange('plan')}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[color:var(--hairline)] bg-white text-sm text-foreground focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/15 transition-[border-color,box-shadow] duration-200"
                      data-testid="lead-plan">
                      <option value="">Not sure yet — help me decide</option>
                      {planOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                </div>

                {error && <p className="mt-3 text-sm text-rose-600" data-testid="lead-error">{error}</p>}

                <button type="submit" disabled={submitting}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-brand-orange text-white text-sm font-bold hover:bg-brand-orange-dark focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/30 disabled:opacity-50 transition-[background-color,box-shadow] duration-200"
                  data-testid="lead-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {submitting ? 'Sending…' : 'Get my free consultation'} {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>
                <p className="mt-3 text-[11px] text-muted-foreground text-center">We'll only use your details to contact you about your enquiry.</p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
